import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import {
  base_url,
  notifyError,
} from "../General/Notification";
import "./InventoryBalanceCard.css";

function InventoryBalanceCard({ langmiamed, t }) {
  const [reps, setReps] = useState([]);
  const [loadingReps, setLoadingReps] = useState(false);
  const [medicalRepId, setMedicalRepId] = useState("");
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState(null);
  const [section, setSection] = useState("samples");

  const token = localStorage.getItem("tokenMiamed");
  const dir = langmiamed === "ar" ? "rtl" : "ltr";
  const isRtl = langmiamed === "ar";

  const loadReps = useCallback(() => {
    if (!token) return;
    setLoadingReps(true);
    axios
      .get(`${base_url}api/medical-reps`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const list = res?.data?.data ?? res?.data ?? [];
        setReps(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        notifyError(
          err?.response?.data?.message || t("err") || "Error loading representatives"
        );
      })
      .finally(() => setLoadingReps(false));
  }, [token, t]);

  useEffect(() => {
    loadReps();
  }, [loadReps]);

  const loadCard = (e) => {
    e?.preventDefault?.();
    if (!medicalRepId) {
      notifyError(t("Select a medical representative") || "Select a medical representative");
      return;
    }
    setLoading(true);
    setCard(null);
    axios
      .get(`${base_url}api/medical-rep/inventory-balance-card/${medicalRepId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setCard(res?.data?.data ?? null);
      })
      .catch((err) => {
        notifyError(
          err?.response?.data?.message || t("err") || "Error loading balance"
        );
      })
      .finally(() => setLoading(false));
  };

  const repLabel = (rep) => {
    if (!rep) return "";
    if (rep.name) return rep.name;
    if (rep.user_name) return rep.user_name;
    const full = `${rep.first_name || ""} ${rep.last_name || ""}`.trim();
    return full || rep.email || `#${rep.id}`;
  };

  const repOptions = useMemo(
    () =>
      (reps || []).map((rep) => ({
        value: String(rep.id),
        label: repLabel(rep),
      })),
    [reps]
  );

  const selectedRep = useMemo(
    () => repOptions.find((o) => o.value === String(medicalRepId)) || null,
    [repOptions, medicalRepId]
  );

  const filterOption = (option, rawInput) => {
    const q = (rawInput || "").trim().toLowerCase();
    if (!q) return true;
    return String(option?.label ?? "")
      .toLowerCase()
      .includes(q);
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 8,
      borderColor: state.isFocused ? "#0b6e4f" : "#cfd8e3",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(11, 110, 79, 0.15)" : "none",
      minHeight: 40,
      fontSize: "0.92rem",
      cursor: "text",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6b7a8d",
      textAlign: isRtl ? "right" : "left",
    }),
    singleValue: (provided) => ({
      ...provided,
      textAlign: isRtl ? "right" : "left",
    }),
    input: (provided) => ({
      ...provided,
      textAlign: isRtl ? "right" : "left",
    }),
    option: (provided) => ({
      ...provided,
      textAlign: isRtl ? "right" : "left",
      fontSize: "0.9rem",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 30,
      textAlign: isRtl ? "right" : "left",
    }),
  };

  const rows = useMemo(() => {
    if (!card) return [];
    return section === "samples" ? card.samples || [] : card.marketing_tools || [];
  }, [card, section]);

  const summary = card?.summary;

  return (
    <div className="inv-balance" dir={dir}>
      <p className="inv-balance__hint">{t("balance_card_hint")}</p>

      <form className="inv-balance__form" onSubmit={loadCard}>
        <label className="inv-balance__label">
          {t("Select a medical representative")}
        </label>
        <div className="inv-balance__row">
          <div className="inv-balance__select-wrap">
            <Select
              classNamePrefix="inv-balance-select"
              options={repOptions}
              value={selectedRep}
              onChange={(opt) => setMedicalRepId(opt ? String(opt.value) : "")}
              isSearchable
              isClearable
              isDisabled={loadingReps}
              isLoading={loadingReps}
              filterOption={filterOption}
              placeholder={
                loadingReps
                  ? t("loading") || "…"
                  : t("search_medical_rep") || t("Select a medical representative")
              }
              noOptionsMessage={() => t("no_data") || t("NoData") || "—"}
              styles={selectStyles}
            />
            <p className="inv-balance__search-hint">{t("search_medical_rep_hint")}</p>
          </div>
          <button type="submit" className="inv-balance__btn" disabled={loading}>
            {loading ? t("loading") || "…" : t("balance_card_show")}
          </button>
        </div>
      </form>

      {card && (
        <>
          <div className="inv-balance__summary">
            <div className="inv-balance__stat">
              <span className="inv-balance__stat-label">{t("delegates")}</span>
              <strong>{card.medical_rep?.name || card.medical_rep?.user_name}</strong>
            </div>
            <div className="inv-balance__stat inv-balance__stat--in">
              <span className="inv-balance__stat-label">{t("balance_card_samples_remaining")}</span>
              <strong>{summary?.samples_remaining_units ?? 0}</strong>
              <small>
                {summary?.samples_with_remaining ?? 0} {t("balance_card_items")}
              </small>
            </div>
            <div className="inv-balance__stat inv-balance__stat--out">
              <span className="inv-balance__stat-label">{t("balance_card_tools_remaining")}</span>
              <strong>{summary?.tools_remaining_units ?? 0}</strong>
              <small>
                {summary?.tools_with_remaining ?? 0} {t("balance_card_items")}
              </small>
            </div>
          </div>

          <div className="inv-balance__tabs">
            <button
              type="button"
              className={
                section === "samples"
                  ? "inv-balance__tab inv-balance__tab--active"
                  : "inv-balance__tab"
              }
              onClick={() => setSection("samples")}
            >
              {t("get_products_stock_report")}
            </button>
            <button
              type="button"
              className={
                section === "tools"
                  ? "inv-balance__tab inv-balance__tab--active"
                  : "inv-balance__tab"
              }
              onClick={() => setSection("tools")}
            >
              {t("get_markitings_stock_report")}
            </button>
          </div>

          <div className="inv-balance__table-wrap">
            <table className="inv-balance__table">
              <thead>
                <tr>
                  <th>{t("name") || t("product_name") || "Name"}</th>
                  <th>{t("batch_number")}</th>
                  <th>{t("balance_card_inbound")}</th>
                  <th>{t("balance_card_outbound")}</th>
                  <th>{t("balance_card_remaining")}</th>
                  <th>{t("balance_card_over")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="inv-balance__empty">
                      {t("no_data") || t("NoData") || "—"}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr
                      key={`${row.id}-${row.batch_number ?? ""}`}
                      className={row.remaining > 0 ? "" : "inv-balance__row--zero"}
                    >
                      <td>{row.name}</td>
                      <td>{row.batch_number || t("work_plan_stock_no_batch") || "—"}</td>
                      <td className="num inbound">{row.inbound}</td>
                      <td className="num outbound">{row.outbound}</td>
                      <td className="num remaining">{row.remaining}</td>
                      <td className="num over">{row.over_delivery || 0}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {Array.isArray(card.legacy_unallocated_deliveries) &&
            card.legacy_unallocated_deliveries.length > 0 && (
              <div className="inv-balance__legacy">
                <h4>{t("balance_card_legacy_title")}</h4>
                <ul>
                  {card.legacy_unallocated_deliveries.map((item) => (
                    <li key={item.product_id}>
                      {item.product?.name || item.product_id}:{" "}
                      {item.quantity_delivered_unallocated}
                    </li>
                  ))}
                </ul>
              </div>
            )}
        </>
      )}

      {!card && !loading && (
        <div className="inv-balance__placeholder">
          <p>{t("balance_card_placeholder")}</p>
        </div>
      )}
    </div>
  );
}

export default InventoryBalanceCard;
